import { useEffect, useState } from 'react';
import axios from 'axios';
import moment from 'moment';

const apiUrl = process.env.REACT_APP_API_URL ?? '';

const WARNING_THRESHOLD_DAYS = 7;

export interface BarcodeExpiryInfo {
  /** 'warning' = 7 days or fewer remaining (including today). 'expired' = already past. */
  status: 'warning' | 'expired';
  /** The raw ISO date string as returned by the API. */
  validUntil: string;
  /** Date formatted as DD.MM.YYYY, for display in messages. */
  displayDate: string;
  /** Whole calendar days remaining. Negative once expired. */
  daysRemaining: number;
}

// ─── Module-level singleton: ONE fetch shared across every mounted Navbar ───
// ─── instance (the Navbar mounts in more than one layout). ──────────────────

let _info: BarcodeExpiryInfo | null = null;
let _fetched = false;
const _subscribers = new Set<(info: BarcodeExpiryInfo | null) => void>();
const _inflight = { promise: null as Promise<void> | null };

const computeInfo = (validUntil: string | null | undefined): BarcodeExpiryInfo | null => {
  if (!validUntil) return null;
  const parsed = moment(validUntil);
  if (!parsed.isValid()) return null;

  const today = moment().startOf('day');
  const target = parsed.clone().startOf('day');
  const daysRemaining = target.diff(today, 'days');

  if (daysRemaining > WARNING_THRESHOLD_DAYS) return null;

  return {
    status: daysRemaining < 0 ? 'expired' : 'warning',
    validUntil,
    displayDate: parsed.format('DD.MM.YYYY'),
    daysRemaining,
  };
};

const notifySubscribers = () => {
  _subscribers.forEach((cb) => cb(_info));
};

const fetchInfo = (): Promise<void> => {
  const token = localStorage.getItem('token');
  if (!token) return Promise.resolve();

  if (_inflight.promise) return _inflight.promise;

  _inflight.promise = axios
    // Low-privilege endpoint gated on BARCODE_ALERTS (not the admin-only
    // COMPANY_SETTINGS module) — returns only { barcodeValidUntil }.
    .get(`${apiUrl}/api/company-settings/barcode-expiry`, {
      headers: { 'x-auth-token': token },
    })
    .then((res) => {
      _info = computeInfo(res.data?.barcodeValidUntil);
      _fetched = true;
      notifySubscribers();
    })
    .catch(() => {
      // Silent — nothing to warn about on error.
      _info = null;
      _fetched = true;
      notifySubscribers();
    })
    .finally(() => {
      _inflight.promise = null;
    });

  return _inflight.promise;
};

const subscribe = (cb: (info: BarcodeExpiryInfo | null) => void) => {
  const isFirst = _subscribers.size === 0;
  _subscribers.add(cb);
  if (isFirst && !_fetched) {
    fetchInfo();
  } else {
    // Not first (or already fetched once) — deliver current value immediately for instant paint.
    cb(_info);
  }
  return () => {
    _subscribers.delete(cb);
  };
};

const useBarcodeExpiry = (): BarcodeExpiryInfo | null => {
  const [info, setInfo] = useState<BarcodeExpiryInfo | null>(_info);

  useEffect(() => {
    const unsubscribe = subscribe(setInfo);
    return unsubscribe;
  }, []);

  return info;
};

export default useBarcodeExpiry;
